import { ConfirmAction } from "@atlas/composite";
import { Button, useToast, WithToolTip } from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHubPackage } from "@models";
import { useUpdateHubPackage } from "@query";
import { ArrowRightLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";

export interface IPackageActionsBarProps {
  cartPackage: IHubPackage | null | undefined;
  itemCount: number;
  onEdit?: () => void;
  onCreate?: () => void;
  onSwitch: () => void;
  buttonClassName?: string;
  iconClassName?: string;
}

export function PackageActionsBar({
  cartPackage,
  itemCount,
  onEdit,
  onCreate,
  onSwitch,
  buttonClassName,
  iconClassName,
}: IPackageActionsBarProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [emptyConfirmOpen, setEmptyConfirmOpen] = useState(false);

  const { mutateAsync: updateHubPackage, isPending: isUpdating } =
    useUpdateHubPackage();

  const handleEmptyPackage = useCallback(async () => {
    if (!cartPackage) return;
    try {
      await updateHubPackage({ ...cartPackage, catalogRelations: [] });
      toast.add({
        title: t("hub.checkout.empty_package_success", {
          defaultValue: "Package emptied",
        }),
        data: { type: "success" },
      });
      setEmptyConfirmOpen(false);
    } catch {
      toast.add({
        title: t("hub.checkout.empty_package_error", {
          defaultValue: "Could not empty package",
        }),
        data: { type: "error" },
      });
    }
  }, [cartPackage, updateHubPackage, toast, t]);

  return (
    <>
      {onEdit && (
        <WithToolTip
          tooltipText={t("hub.package_manager.edit_title", {
            defaultValue: "Edit Package",
          })}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={buttonClassName}
            onClick={onEdit}
            aria-label={t("hub.package_manager.edit_title", {
              defaultValue: "Edit package",
            })}>
            <Pencil className={iconClassName || "size-4"} aria-hidden />
          </Button>
        </WithToolTip>
      )}
      <WithToolTip
        tooltipText={t("hub.package_manager.switch_package", {
          defaultValue: "Switch Package",
        })}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={buttonClassName}
          onClick={onSwitch}
          aria-label={t("hub.package_manager.switch_package", {
            defaultValue: "Switch Package",
          })}>
          <ArrowRightLeft className={iconClassName || "size-4"} aria-hidden />
        </Button>
      </WithToolTip>
      {onCreate && (
        <WithToolTip
          tooltipText={t("hub.package_manager.create_aria", {
            defaultValue: "Create new package",
          })}>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={buttonClassName}
            onClick={onCreate}
            aria-label={t("hub.package_manager.create_aria", {
              defaultValue: "Create new package",
            })}>
            <Plus className={iconClassName || "size-4"} aria-hidden />
          </Button>
        </WithToolTip>
      )}
      <WithToolTip
        tooltipText={t("hub.checkout.empty_package", {
          defaultValue: "Empty Package",
        })}>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={buttonClassName}
          disabled={isUpdating || itemCount === 0}
          onClick={() => setEmptyConfirmOpen(true)}
          aria-label={t("hub.checkout.empty_package", {
            defaultValue: "Empty Package",
          })}>
          <Trash2 className={iconClassName || "size-4"} aria-hidden />
        </Button>
      </WithToolTip>

      <ConfirmAction
        open={emptyConfirmOpen}
        onOpenChange={setEmptyConfirmOpen}
        title={t("hub.checkout.empty_package_confirm_title", {
          defaultValue: "Remove all Catalogs from package?",
        })}
        description={t("hub.checkout.empty_package_confirm_description", {
          packageName:
            (cartPackage?.label ?? cartPackage?.name ?? "").trim() || "package",
          defaultValue:
            "Are you sure you want to remove all catalogs from package {{packageName}}?",
        })}
        confirmText={t("hub.checkout.empty_package", {
          defaultValue: "Empty Package",
        })}
        cancelText={t("hub.administer.cancel", {
          defaultValue: "Cancel",
        })}
        actionVariant="destructive"
        onConfirm={handleEmptyPackage}
      />
    </>
  );
}
